import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'

import { bankAccountPath, dashboardPath } from 'components/Routes'
import { useAuth } from 'components/AuthProvider'
import { useWallet } from 'hooks/useWallet'
import { useMutateRequestWithdrawal } from 'hooks/useWithdrawals'

import Button from 'components/Button'
import Currency from 'components/Currency'
import DefaultLayout from 'components/layout/DefaultLayout'
import OnboardingMessage from 'components/OnboardingMessage'
import { Grid2, TextField, Typography } from '@mui/material'

import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'

const useStyles = makeStyles()((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(2)
  },
  voucherFunds: {
    marginBottom: theme.spacing(2)
  },
  walletBalanceGroup: {
    margin: theme.spacing(2, 2),
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '1.25rem'
  }
}))

export default function WithdrawFunds(): JSX.Element {
  const { classes } = useStyles()
  const navigate = useNavigate()

  const [amountToWithdraw, setAmountToWithdraw] = useState<string>('')
  const [isWithdrawalRequested, setIsWithdrawalRequested] =
    useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [popUpOpen, setPopUpOpen] = useState<boolean>(false)
  const { user, maybeRequestOtp, setToast } = useAuth()

  const { data } = useWallet()

  const requestWithdrawal = useMutateRequestWithdrawal()

  const voucherBalance = useMemo(() => {
    return data?.expirableWalletCreditBalance ?? 0
  }, [data])

  const walletBalance = useMemo(() => {
    return data?.walletBalance ?? 0
  }, [data])

  const isOnBoarded = useMemo(() => {
    const { profile } = user ?? {}
    return profile?.onboarded
  }, [user])

  const withdrawableAmount = useMemo(() => {
    return data?.withdrawableAmount ?? 0
  }, [data])

  const withheldFunds = useMemo(() => {
    return data?.withheldFunds ?? 0
  }, [data])

  // If the user is not `otpVerified` when they attempt to request a withdrawal, we set the `isWithdrawalRequested`
  // flag. Then, once the user becomes `otpVerified` by completing the OTP generation and verification flow,
  // we can reset this flag and attemp the withdrawal request again
  useEffect(() => {
    if (isWithdrawalRequested && user?.otpVerified) {
      setIsWithdrawalRequested(false)

      // TODO: bankAccountId is not yet available in the user profile REST response.
      // The backend requires a valid bank_account_id. This will need a bank account
      // lookup endpoint or profile expansion before this flow works end-to-end.
      requestWithdrawal.mutate(
        { amount: Number(amountToWithdraw), bankAccountId: 0 },
        {
          onSuccess: () => {
            setToast({
              open: true,
              message: 'Good as gold! Withdrawal requested.',
              type: 'success',
              duration: 3000
            })
            navigate(dashboardPath())
          },
          onError: (error: unknown) =>
            setToast({
              open: true,
              message:
                error instanceof Error ? error.message : 'Withdrawal failed',
              type: 'error',
              duration: 3000
            })
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWithdrawalRequested, user])

  function handleChangeAmountToWithdraw(event: ChangeEvent<HTMLInputElement>) {
    setAmountToWithdraw(event.target.value)
    if (!event.target.value) setErrorMessage('Amount is required')
    else if (Number(event.target.value) > Number(walletBalance))
      setErrorMessage(`You don't have enough funds`)
    else setErrorMessage('')
  }

  // function handleSubmit(event: FormEvent<HTMLFormElement>) {
  function handleSubmit() {
    // TODO: form validation and required fields, (once we have firmed up flow/designs)
    // event.preventDefault()

    if (!amountToWithdraw) {
      return setErrorMessage('Amount is required')
    }

    if (user?.otpVerified) {
      // TODO: bankAccountId is not yet available in the user profile REST response.
      // The backend requires a valid bank_account_id. This will need a bank account
      // lookup endpoint or profile expansion before this flow works end-to-end.
      requestWithdrawal.mutate(
        { amount: Number(amountToWithdraw), bankAccountId: 0 },
        {
          onSuccess: () => {
            setToast({
              open: true,
              message: 'Good as gold! Withdrawal requested.',
              type: 'success',
              duration: 3000
            })
            navigate(dashboardPath())
          },
          onError: (error: unknown) =>
            setToast({
              open: true,
              message:
                error instanceof Error ? error.message : 'Withdrawal failed',
              type: 'error',
              duration: 3000
            })
        }
      )
    } else {
      setIsWithdrawalRequested(true)
      maybeRequestOtp()
    }
  }

  function handlePopUpOpen() {
    // event.preventDefault()
    setPopUpOpen(true)
  }

  function handlePopUpClose() {
    setPopUpOpen(false)

    handleSubmit()
  }

  const noBankAccount = useMemo(() => !user?.profile?.bankAccountNumber, [user])

  useEffect(() => {
    if (noBankAccount) {
      navigate(bankAccountPath())
    }
  }, [noBankAccount, navigate])

  const renderHeader = useCallback(() => {
    return (
      <Typography component='h1' variant='h1' fontSize='4.5rem' color='primary'>
        Withdraw funds
      </Typography>
    )
  }, [])

  return (
    <DefaultLayout
      backgroundColor='default'
      heading={renderHeader()}
      showBackIcon
    >
      <Dialog
        open={popUpOpen}
        onClose={handlePopUpClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {'I understand it can take up to 3 working days to receive my funds'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            FYI - Goldie actions withdrawal requests within 1 working day but it
            depends how quickly your bank actions our request.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePopUpClose} autoFocus>
            I understand
          </Button>
        </DialogActions>
      </Dialog>
      {noBankAccount ? null : isOnBoarded ? (
        <form className={classes.form} noValidate onSubmit={handlePopUpOpen}>
          <Grid2 container spacing={2}>
            <Grid2 className={classes.walletBalanceGroup} size={{ xs: 12 }}>
              <Typography component='h2' variant='h6'>
                Wallet balance
              </Typography>
              <Typography component='p' variant='h2'>
                <Currency amount={walletBalance} isSuperscript />
              </Typography>
              <Typography component='p'>
                Gifts balance: <Currency amount={voucherBalance} />
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField
                defaultValue={
                  user?.profile?.bankAccountNumber ??
                  'Deposit funds first to verify your bank account'
                }
                disabled
                fullWidth
                id='accountNumber'
                label='Account number'
                name='accountNumber'
                required
                type='text'
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField
                autoFocus
                fullWidth
                id='amountToWithdraw'
                label='Amount'
                name='amountToWithdraw'
                onChange={handleChangeAmountToWithdraw}
                required
                // TODO: this doesn't actually seem to be a number - can type letters??
                // Also, restrict this to 2dp (and everywhere else)
                type='number'
                value={amountToWithdraw}
                error={!!errorMessage}
                helperText={errorMessage}
              />
            </Grid2>

            <Grid2 mt={2} size={{ xs: 12 }}>
              <Typography>
                Withdrawable amount: ${withdrawableAmount}*
              </Typography>
            </Grid2>
            <Grid2 my={2} size={{ xs: 12 }}>
              <Typography variant='body2' color='GrayText'>
                Withheld amount: ${withheldFunds}
              </Typography>
              <Typography variant='body2' color='GrayText'>
                Any promotional or referral credits can not be withdrawn for 12
                months from the date the credit was applied.
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <Button
                color='primary'
                disabled={
                  requestWithdrawal.isLoading ||
                  !user?.profile?.bankAccountNumber ||
                  Number(amountToWithdraw) <= 0 ||
                  Number(amountToWithdraw) > withdrawableAmount
                }
                fullWidth
                onClick={handlePopUpOpen}
              >
                Withdraw funds
              </Button>
            </Grid2>
          </Grid2>
        </form>
      ) : (
        <OnboardingMessage />
      )}
    </DefaultLayout>
  )
}
